import React from 'react';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaHeadset,
  FaUser,
  FaMinusCircle,
} from 'react-icons/fa';

/*
 * Assessment results used to render as `• Label: value` bullet dumps, which put the
 * criterion name, its verdict, the reason and the transcript evidence all at the same
 * visual weight. These components give each of those a distinct treatment so a reviewer
 * can scan a long scorecard and land on the failures first.
 */

const TONES = {
  passed: {
    pill: 'bg-green-50 text-green-700 border-green-200',
    accent: 'border-l-green-500',
    icon: <FaCheckCircle />,
  },
  failed: {
    pill: 'bg-red-50 text-red-700 border-red-200',
    accent: 'border-l-red-500',
    icon: <FaTimesCircle />,
  },
  critical: {
    pill: 'bg-red-50 text-red-700 border-red-200',
    accent: 'border-l-red-500',
    icon: <FaExclamationTriangle />,
  },
  neutral: {
    pill: 'bg-gray-100 text-gray-600 border-gray-200',
    accent: 'border-l-gray-300',
    icon: <FaMinusCircle />,
  },
};

export const StatusPill = ({ tone = 'neutral', children }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${t.pill}`}
    >
      {t.icon}
      {children}
    </span>
  );
};

/* A scored criterion reads faster as a filled track than as "8 / 10". */
export const ScoreMeter = ({ score, max, tone = 'neutral' }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (score / max) * 100)) : 0;
  const fill =
    tone === 'failed' || tone === 'critical'
      ? 'bg-red-500'
      : pct >= 80
      ? 'bg-green-500'
      : pct >= 50
      ? 'bg-amber-500'
      : 'bg-red-500';

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-semibold leading-none text-gray-900">{score}</span>
        <span className="text-xs text-gray-500">/ {max}</span>
      </div>
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/* Transcript evidence is a quote, so it gets quote treatment rather than a bullet. */
export const EvidenceQuote = ({ speaker, children }) => {
  const isAgent = speaker === 'agent';
  return (
    <div className="flex gap-2.5">
      <span
        className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs ${
          isAgent ? 'bg-cloudza-100 text-cloudza-700' : 'bg-gray-100 text-gray-500'
        }`}
        aria-hidden="true"
      >
        {isAgent ? <FaHeadset /> : <FaUser />}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {isAgent ? 'Agent' : 'Customer'}
        </div>
        <p className="text-sm italic text-gray-700 break-words">&ldquo;{children}&rdquo;</p>
      </div>
    </div>
  );
};

const AssessmentCriterion = ({
  name,
  question,
  tone = 'neutral',
  statusLabel,
  critical = false,
  answer,
  expected,
  score,
  max,
  reason,
  evidence = {},
}) => {
  const t = TONES[tone] || TONES.neutral;
  const hasEvidence = evidence.agent || evidence.customer;
  const hasScore = score !== undefined && max !== undefined;

  return (
    <li className={`card !p-0 overflow-hidden border-l-4 ${t.accent}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5">
        <div className="min-w-0">
          <h6 className="text-sm font-semibold capitalize text-gray-900">{name}</h6>
          {question && <p className="mt-0.5 text-xs italic text-gray-500">{question}</p>}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {critical && <StatusPill tone="critical">Critical</StatusPill>}
          {statusLabel && <StatusPill tone={tone}>{statusLabel}</StatusPill>}
        </div>
      </div>

      {(hasScore || answer) && (
        <div className="mt-3 px-4">
          {hasScore && <ScoreMeter score={score} max={max} tone={tone} />}
          {answer && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <span>
                Answered{' '}
                <span
                  className={`font-semibold ${
                    tone === 'passed' ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {answer.toUpperCase()}
                </span>
              </span>
              {expected && <span>Expected {expected.toUpperCase()}</span>}
            </div>
          )}
        </div>
      )}

      {reason && <p className="mt-3 px-4 text-sm leading-relaxed text-gray-700">{reason}</p>}

      {hasEvidence && (
        <div className="mt-3 space-y-2.5 border-t border-gray-100 bg-gray-50 px-4 py-3">
          {evidence.agent && <EvidenceQuote speaker="agent">{evidence.agent}</EvidenceQuote>}
          {evidence.customer && (
            <EvidenceQuote speaker="customer">{evidence.customer}</EvidenceQuote>
          )}
        </div>
      )}

      {!hasEvidence && <div className="pb-3.5" />}
    </li>
  );
};

export default AssessmentCriterion;
